import { HttpParams, HttpParameterCodec } from '@angular/common/http';
import { CustomHttpParameterCodec, IdentityHttpParameterCodec } from './encoder';

export enum QueryParamStyle {
    Json,
    Form,
    DeepObject,
    SpaceDelimited,
    PipeDelimited,
}

export type Delimiter = "," | " " | "|" | "\t";

export interface ParamOptions {
    explode?: boolean;
    delimiter?: Delimiter;
}

interface ParamEntry {
    values: string[];
    options: Required<ParamOptions>;
}

export class OpenApiHttpParams {
    private params: Map<string, ParamEntry> = new Map();
    private defaults: Required<ParamOptions>;
    private encoder: HttpParameterCodec;

    constructor(encoder?: HttpParameterCodec, defaults?: { explode?: boolean; delimiter?: Delimiter }) {
        this.encoder = encoder || new CustomHttpParameterCodec();
        this.defaults = { explode: defaults?.explode ?? true, delimiter: defaults?.delimiter ?? "," };
    }

    private resolveOptions(local?: ParamOptions): Required<ParamOptions> {
        return { explode: local?.explode ?? this.defaults.explode, delimiter: local?.delimiter ?? this.defaults.delimiter };
    }

    set(key: string, values: string[] | string, options?: ParamOptions): this {
        const arr = Array.isArray(values) ? values.slice() : [values];
        this.params.set(key, { values: arr, options: this.resolveOptions(options) });
        return this;
    }

    append(key: string, value: string, options?: ParamOptions): this {
        const entry = this.params.get(key);
        if (entry) {
            if (options) entry.options = this.resolveOptions({ ...entry.options, ...options });
            entry.values.push(value);
        } else {
            this.set(key, [value], options);
        }
        return this;
    }

    appendAll(key: string, values: Array<string | number | boolean>, options?: ParamOptions): this {
        values.forEach((value) => this.append(key, String(value), options));
        return this;
    }

    toRecord(): Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>> {
        const parts: Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>> = {};
        for (const [key, entry] of this.params.entries()) {
            const encodedKey = this.encoder.encodeKey(key);
            if (entry.options.explode) {
                parts[encodedKey] = entry.values.map((v) => this.encoder.encodeValue(v));
            } else {
                parts[encodedKey] = entry.values.map((v) => this.encoder.encodeValue(v)).join(entry.options.delimiter);
            }
        }
        return parts;
    }

    toHttpParams(): HttpParams {
        return new HttpParams({ encoder: new IdentityHttpParameterCodec() }).appendAll(this.toRecord());
    }
}

export function concatHttpParamsObject(httpParams: OpenApiHttpParams, key: string, item: { [index: string]: any }, delimiter: Delimiter): OpenApiHttpParams {
    let keyAndValues: string[] = [];
    for (const k in item) {
        keyAndValues.push(k);
        const value = item[k];
        if (Array.isArray(value)) {
            keyAndValues.push(...value.map(convertToString));
        } else {
            keyAndValues.push(convertToString(value));
        }
    }
    return httpParams.set(key, keyAndValues, { explode: false, delimiter });
}

function convertToString(value: any): string {
    return value instanceof Date ? value.toISOString() : value.toString();
}
